import { useState, useMemo, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { useVirtualizer } from "@tanstack/react-virtual";
import {
  Paper,
  Title,
  Group,
  SegmentedControl,
  Button,
  Text,
  Box,
  useMantineTheme,
  Table,
  ScrollArea,
  Flex,
  Skeleton,
  Modal,
  Badge,
  Anchor,
  Divider,
  Select,
  Grid,
  SimpleGrid,
  HoverCard,
  ActionIcon,
} from "@mantine/core";
import { useApiUrl, useCustom } from "@refinedev/core";
import {
  IconDownload,
  IconExternalLink,
  IconInfoCircle,
} from "@tabler/icons-react";
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import axiosInstance from "axiosInstance";

// ─── Types ───────────────────────────────────────────────────────────────────

type YearPreset = string;

type AnalyticsDataPoint = {
  date: string;
  new_subscriptions: number;
  renewals: number;
  active_subscriptions: number;
  cancellations: number;
  churn_rate: number;
  revenue: number;
  loss_revenue: number;
  tea_addon_count?: number;
  addon_name?: string | null;
};

type AnalyticsSummary = {
  total_active_subscribers: number;
  total_renewals: number;
  total_new_subscriptions: number;
  total_cancellations: number;
  average_churn_rate: number;
  total_revenue: number;
  total_loss_revenue: number;
};

type AnalyticsResponse = {
  summary: AnalyticsSummary;
  data: AnalyticsDataPoint[];
};

type PeriodMeta = {
  label: string;
  start: string;
  end: string;
  date_range: string;
  tea_addon_id: string | null;
};

type CellData = {
  active: number;
  addon_canceled: number;
  box_canceled: number;
  churn_rate: number;
  actual_tea_addons: number;
  addon_chain: number;
};

type CellDetailsSub = {
  number: number;
  id: number;
  name: string;
  email: string;
  addon_status: string;
  addon_started_at: string;
  addon_canceled_at: string | null;
  addon_canceled: boolean;
  box_status: string;
  box_canceled_at: string | null;
  box_canceled: boolean;
  chargebee_subscription_id: string;
  chargebee_url: string;
  plan_name: string | null;
  plan_brand: string | null;
  tea_addons: {
    id: number;
    name: string;
    chargebee_addon_id: string;
    shippable_at: string;
    box_month: string;
  }[];
};

type CellDetailsResponse = {
  subscriptions: CellDetailsSub[];
  total_count: number;
  page: number;
  per_page: number;
  total_pages: number;
};

type SelectedCell = {
  cohort: Cohort;
  cohortMeta: PeriodMeta;
  periodMeta: PeriodMeta;
} | null;

type Cohort = {
  start_period: string;
  start_count: number;
  breakdown: Record<string, CellData>;
};

type ChurnBreakdownResponse = {
  periods: PeriodMeta[];
  cohorts: Cohort[];
  totals: Record<string, number>;
  actual_tea_addons_totals: Record<string, number>;
  addon_chain_totals: Record<string, number>;
};

type ViewMode = "all" | "addon-only" | "box-canceled";
type DisplayMode = "counts" | "rates" | "actual_addons" | "addon_chain";
type Lang = "en" | "ja";

// ─── Translations ────────────────────────────────────────────────────────────

const T = {
  en: {
    pageTitle: "Tea Addon Subscription Analytics",
    downloadCsv: "Download CSV",
    all: "All",
    custom: "Custom",
    fromBoxMonth: "From box month",
    toBoxMonth: "To box month",
    // KPI cards
    activeSubscribers: "Active Subscribers",
    totalRenewals: "Total Renewals",
    newSubscriptions: "New Subscriptions",
    cancellations: "Cancellations",
    avgChurnRate: "Avg Churn Rate",
    total: "Total",
    // Graph titles
    activeSubscriptionsChart: "Active Subscriptions",
    newSubscriptionsChart: "New Subscriptions",
    renewalsChart: "Renewals",
    revenueChart: "Revenue",
    lossRevenueChart: "Loss Revenue",
    cancellationsChart: "Cancellations",
    churnRateChart: "Churn Rate",
    teaAddonsCreatedChart: "Tea Addons Created",
    // Churn breakdown
    churnBreakdown: "Churn Breakdown",
    activeCounts: "Active Counts",
    churnPct: "Churn %",
    actualAddons: "Actual Addons",
    addonChain: "Addon Chain",
    // View tabs
    allCancellations: "All Churn",
    allCancellationsDesc: "All reasons combined",
    allCancellationsBanner:
      "Retention after all cancellations. The numbers show how many subscribers from each cohort are still active, regardless of why others left.",
    addonCanceledBoxActive: "Addon Churn Only",
    addonCanceledBoxActiveDesc: "Dropped the addon, kept the box",
    addonCanceledBoxActiveBanner:
      "Retention counting only addon-specific churn. People who chose to cancel just the tea addon (while keeping their box) reduce the count. Box cancellations are excluded.",
    addonCanceledBoxCanceled: "Box Churn Only",
    addonCanceledBoxCanceledDesc: "Left the box entirely",
    addonCanceledBoxCanceledBanner:
      "Retention counting only box churn. People who canceled their entire box subscription reduce the count. Addon-only cancellations are excluded.",
    // Table
    cohort: "Cohort",
    start: "Start",
    churnSuffix: "churn",
    remainingSuffix: "remaining",
    // Tooltip
    teaAddon: "Tea addon",
    period: "Period",
    view: "View",
    cumulativeFromStart: "Cumulative from start",
    remaining: "Remaining",
    addonCanceledLabel: "Addon churn (box active)",
    boxCanceledLabel: "Box churn (left entirely)",
    totalCanceled: "Total canceled",
    churnRate: "Churn rate",
    changeThisPeriod: "Change this period",
    newCancellations: "New cancellations",
    noChange: "no change",
    // Modal
    subscriptions: "subscriptions",
    subscription: "subscription",
    loading: "Loading...",
    of: "of",
    inThisCohort: "in this cohort",
    withTeaAddons: "With Tea Addons",
    withoutTeaAddons: "Without Tea Addons",
    // Modal row
    addonCanceled: "Addon Canceled",
    addonActive: "Addon Active",
    boxCanceled: "Box Canceled",
    boxActive: "Box Active",
    addon: "Addon",
    started: "Started",
    canceled: "Canceled",
    box: "Box",
    status: "Status",
    plan: "Plan",
    teaAddons: "Tea Addons",
    noMatchingTeaAddon: "No matching tea addon for this period",
    shippable: "shippable",
    chargebee: "Chargebee",
    // Help texts
    helpChurnBreakdown:
      "This table tracks how groups of subscribers (cohorts) change over time. Each row is a group of people who subscribed in the same month. Reading left to right shows what happened to that group in later months.",
    helpActiveCounts:
      "Shows how many subscribers from each group are still active. The number goes down as people cancel.",
    helpChurnPct:
      "Shows the percentage of subscribers who have canceled. Higher percentage = more people left.",
    helpActualAddons:
      "Shows the actual number of tea addon products created for each group, regardless of whether the subscription was canceled.",
    helpAddonChain:
      "Shows how many subscribers have received a tea addon every single month without a gap. If someone misses one month, they are removed permanently.",
    helpAllCancellations:
      "The table shows how many subscribers remain active over time. All cancellations — whether someone dropped just the addon or their entire box — reduce the count.",
    helpAddonCanceledBoxActive:
      "The table shows retention against addon-specific churn only. Only people who chose to drop the tea addon (while keeping their box) reduce the count. People who left because they canceled their box are not counted as churn here.",
    helpAddonCanceledBoxCanceled:
      "The table shows retention against box churn only. Only people who canceled their entire box subscription reduce the count. People who dropped just the tea addon are not counted as churn here.",
  },
  ja: {
    pageTitle: "ティーアドオン サブスクリプション分析",
    downloadCsv: "CSVダウンロード",
    all: "全て",
    custom: "カスタム",
    fromBoxMonth: "開始ボックス月",
    toBoxMonth: "終了ボックス月",
    // KPI cards
    activeSubscribers: "アクティブ登録者",
    totalRenewals: "更新合計",
    newSubscriptions: "新規登録",
    cancellations: "キャンセル",
    avgChurnRate: "平均解約率",
    total: "合計",
    // Graph titles
    activeSubscriptionsChart: "アクティブサブスクリプション",
    newSubscriptionsChart: "新規サブスクリプション",
    renewalsChart: "更新",
    revenueChart: "収益",
    lossRevenueChart: "損失収益",
    cancellationsChart: "キャンセル",
    churnRateChart: "解約率",
    teaAddonsCreatedChart: "作成されたティーアドオン",
    // Churn breakdown
    churnBreakdown: "解約分析",
    activeCounts: "アクティブ数",
    churnPct: "解約率 %",
    actualAddons: "実際のアドオン",
    addonChain: "アドオンチェーン",
    // View tabs
    allCancellations: "全解約",
    allCancellationsDesc: "すべての理由を含む",
    allCancellationsBanner:
      "全キャンセル後の継続率。各コホートの登録者が何人まだアクティブかを表示します。理由に関係なくすべてのキャンセルが反映されます。",
    addonCanceledBoxActive: "アドオン解約のみ",
    addonCanceledBoxActiveDesc: "アドオンのみ解約、ボックスは継続",
    addonCanceledBoxActiveBanner:
      "アドオン固有の解約のみを反映した継続率。ティーアドオンのみをキャンセルした人（ボックスは継続）がカウントを減らします。ボックスキャンセルは除外されます。",
    addonCanceledBoxCanceled: "ボックス解約のみ",
    addonCanceledBoxCanceledDesc: "ボックスごと解約",
    addonCanceledBoxCanceledBanner:
      "ボックス解約のみを反映した継続率。ボックスサブスクリプション全体をキャンセルした人がカウントを減らします。アドオンのみのキャンセルは除外されます。",
    // Table
    cohort: "コホート",
    start: "開始",
    churnSuffix: "解約",
    remainingSuffix: "残り",
    // Tooltip
    teaAddon: "ティーアドオン",
    period: "期間",
    view: "表示",
    cumulativeFromStart: "開始からの累計",
    remaining: "残り",
    addonCanceledLabel: "アドオン解約（ボックス継続）",
    boxCanceledLabel: "ボックス解約（全体解約）",
    totalCanceled: "キャンセル合計",
    churnRate: "解約率",
    changeThisPeriod: "この期間の変化",
    newCancellations: "新規キャンセル",
    noChange: "変化なし",
    // Modal
    subscriptions: "件のサブスクリプション",
    subscription: "件のサブスクリプション",
    loading: "読み込み中...",
    of: "/",
    inThisCohort: "",
    withTeaAddons: "ティーアドオンあり",
    withoutTeaAddons: "ティーアドオンなし",
    // Modal row
    addonCanceled: "アドオンキャンセル",
    addonActive: "アドオンアクティブ",
    boxCanceled: "ボックスキャンセル",
    boxActive: "ボックスアクティブ",
    addon: "アドオン",
    started: "開始日",
    canceled: "キャンセル日",
    box: "ボックス",
    status: "ステータス",
    plan: "プラン",
    teaAddons: "ティーアドオン",
    noMatchingTeaAddon: "この期間に一致するティーアドオンはありません",
    shippable: "発送予定",
    chargebee: "Chargebee",
    // Help texts
    helpChurnBreakdown:
      "この表は、登録者グループ（コホート）が時間とともにどのように変化するかを追跡します。各行は同じ月に登録した人々のグループです。左から右に読むと、そのグループの後の月の変化がわかります。",
    helpActiveCounts:
      "各グループのアクティブな登録者数を表示します。キャンセルすると数が減少します。",
    helpChurnPct:
      "キャンセルした登録者の割合を表示します。割合が高いほど、多くの人が離れています。",
    helpActualAddons:
      "サブスクリプションがキャンセルされたかどうかに関係なく、各グループに作成された実際のティーアドオン製品の数を表示します。",
    helpAddonChain:
      "途切れることなく毎月ティーアドオンを受け取った登録者数を表示します。1ヶ月でも欠けると永久に除外されます。",
    helpAllCancellations:
      "時間の経過とともに何人の登録者がアクティブかを表示します。アドオンのみの解約でもボックス全体の解約でも、すべてのキャンセルがカウントを減らします。",
    helpAddonCanceledBoxActive:
      "アドオン固有の解約に対する継続率を表示します。ボックスを継続したままティーアドオンのみ解約した人だけがカウントを減らします。ボックス解約は反映されません。",
    helpAddonCanceledBoxCanceled:
      "ボックス解約に対する継続率を表示します。ボックスサブスクリプション全体を解約した人だけがカウントを減らします。アドオンのみの解約は反映されません。",
  },
} as const;

type Translations = { [K in keyof (typeof T)["en"]]: string };

// ─── Helpers ─────────────────────────────────────────────────────────────────

// The first box month we track is Jan 2025 (Dec 16 2024 – Jan 15 2025).
const FIRST_BOX_START = "2023-12-16";

// Current box month ends on the 15th. If today is past the 15th we're in the
// next box month, so the end is the 15th of next month.
const currentBoxEnd = (): string => {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth(); // 0-based
  if (now.getDate() <= 15) {
    // current box month ends on the 15th of this month
    return `${y}-${String(m + 1).padStart(2, "0")}-15`;
  }
  // we're past the 15th → current box month ends on the 15th of next month
  const nd = new Date(y, m + 1, 15);
  return `${nd.getFullYear()}-${String(nd.getMonth() + 1).padStart(2, "0")}-15`;
};

// Build year options dynamically: 2025 … current box-month year, plus "All".
const buildYearOptions = (): {
  years: string[];
  ranges: Record<string, { from: string; to: string }>;
} => {
  const now = new Date();
  // The "box year" we're in: if we're before Jan 16 we're still in the prev
  // calendar year's Dec box month, but that's the last box of that year.
  const currentBoxYear =
    now.getMonth() === 0 && now.getDate() <= 15
      ? now.getFullYear() - 1
      : now.getFullYear();

  const years: string[] = [];
  const ranges: Record<string, { from: string; to: string }> = {};

  for (let yr = 2024; yr <= currentBoxYear; yr++) {
    const key = String(yr);
    years.push(key);
    ranges[key] = {
      from: `${yr - 1}-12-16`,
      to: `${yr}-12-15`,
    };
  }

  ranges["all"] = { from: FIRST_BOX_START, to: currentBoxEnd() };
  return { years, ranges };
};

const { years: YEAR_OPTIONS, ranges: YEAR_RANGES } = buildYearOptions();

// Box month options for custom range selectors.
// Each box month: "Apr 2025" = { label: "Apr 2025", value: "2025-03-16" }
// value is the period start (16th of prev month).
const MONTH_NAMES = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

type BoxMonthOption = { label: string; value: string };

const buildBoxMonthOptions = (): BoxMonthOption[] => {
  const options: BoxMonthOption[] = [];
  // Start from Jan 2024 box month (Dec 16 2023)
  let y = 2024;
  let m = 1; // 1-based, the "box month" label month

  const now = new Date();
  const curBoxMonth = now.getDate() <= 15 ? now.getMonth() : now.getMonth() + 1; // 0-based
  const curBoxYear =
    curBoxMonth === 12 && now.getMonth() === 11 && now.getDate() > 15
      ? now.getFullYear() + 1
      : now.getDate() <= 15 && now.getMonth() === 0
        ? now.getFullYear() - 1
        : now.getFullYear();
  // Simplified: just go until we pass current date
  const endDate = new Date(now.getFullYear() + 1, 0, 15); // generous upper bound

  while (true) {
    // Period start: 16th of the month before box month
    const prevM = m - 1 === 0 ? 12 : m - 1;
    const prevY = m - 1 === 0 ? y - 1 : y;
    const periodStart = `${prevY}-${String(prevM).padStart(2, "0")}-16`;
    const periodEnd = `${y}-${String(m).padStart(2, "0")}-15`;

    // Stop if period start is past current date
    if (new Date(periodStart) > now) break;

    options.push({
      label: `${MONTH_NAMES[m - 1]} ${y}`,
      value: periodStart,
    });

    m++;
    if (m > 12) {
      m = 1;
      y++;
    }
  }

  return options;
};

const BOX_MONTH_OPTIONS = buildBoxMonthOptions();

// Get the period end date for a box month start value
const boxMonthEnd = (startValue: string): string => {
  const d = new Date(startValue);
  // start is 16th of prev month, end is 15th of next month
  d.setMonth(d.getMonth() + 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-15`;
};

const getCanceled = (cell: CellData, view: ViewMode): number => {
  if (view === "addon-only") return cell.addon_canceled;
  if (view === "box-canceled") return cell.box_canceled;
  return cell.addon_canceled + cell.box_canceled;
};

const getRemaining = (
  startCount: number,
  cell: CellData,
  view: ViewMode,
): number => {
  return startCount - getCanceled(cell, view);
};

const churnColorClass = (churnPct: number): string => {
  if (churnPct <= 5) return "bg-g4";
  if (churnPct <= 10) return "bg-g5";
  if (churnPct <= 20) return "bg-y1";
  if (churnPct <= 35) return "bg-o1";
  if (churnPct <= 55) return "bg-r1";
  return "bg-r2";
};

const COLOR_MAP: Record<string, string> = {
  "bg-g4": "#c6f6d5",
  "bg-g5": "#f0fff4",
  "bg-y1": "#fefcbf",
  "bg-o1": "#fbd38d",
  "bg-r1": "#fed7d7",
  "bg-r2": "#feb2b2",
};

const COLOR_MAP_DARK: Record<string, string> = {
  "bg-g4": "rgba(72, 187, 120, 0.25)",
  "bg-g5": "rgba(72, 187, 120, 0.12)",
  "bg-y1": "rgba(236, 201, 75, 0.2)",
  "bg-o1": "rgba(237, 137, 54, 0.25)",
  "bg-r1": "rgba(245, 101, 101, 0.2)",
  "bg-r2": "rgba(245, 101, 101, 0.35)",
};

const getViewConfig = (t: Translations) => ({
  all: {
    label: t.allCancellations,
    description: t.allCancellationsDesc,
    bannerColor: "blue",
    bannerText: t.allCancellationsBanner,
  },
  "addon-only": {
    label: t.addonCanceledBoxActive,
    description: t.addonCanceledBoxActiveDesc,
    bannerColor: "orange",
    bannerText: t.addonCanceledBoxActiveBanner,
  },
  "box-canceled": {
    label: t.addonCanceledBoxCanceled,
    description: t.addonCanceledBoxCanceledDesc,
    bannerColor: "red",
    bannerText: t.addonCanceledBoxCanceledBanner,
  },
});

// ─── CSV Export ──────────────────────────────────────────────────────────────

const downloadCSV = (
  data: ChurnBreakdownResponse,
  view: ViewMode,
  displayMode: DisplayMode,
  from: string,
  to: string,
) => {
  const { periods, cohorts } = data;
  const headers = [
    "Cohort",
    "Date Range",
    "Tea Addon",
    "Start",
    ...periods.map(
      (p) => `${p.label}${p.tea_addon_id ? ` (${p.tea_addon_id})` : ""}`,
    ),
  ];

  const rows = cohorts.map((cohort) => {
    const meta = periods.find((p) => p.label === cohort.start_period);
    const values = periods.map((p) => {
      const cell = cohort.breakdown[p.label];
      if (!cell) return "";
      const canceled = getCanceled(cell, view);
      const remaining = cohort.start_count - canceled;
      const churnPct =
        cohort.start_count > 0
          ? Math.round((canceled / cohort.start_count) * 100)
          : 0;
      if (displayMode === "rates") return `${churnPct}%`;
      if (displayMode === "actual_addons")
        return String(cell.actual_tea_addons);
      if (displayMode === "addon_chain") return String(cell.addon_chain);
      return String(remaining);
    });
    return [
      cohort.start_period,
      meta?.date_range ?? "",
      meta?.tea_addon_id ?? "",
      String(cohort.start_count),
      ...values,
    ];
  });

  const csvContent = [headers, ...rows]
    .map((row) => row.map((v) => `"${v}"`).join(","))
    .join("\n");
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `churn_breakdown_${view}_${displayMode}_${from}_to_${to}.csv`;
  link.click();
  URL.revokeObjectURL(url);
};

// ─── Portal Tooltip Wrapper ──────────────────────────────────────────────────

const PortalTooltip = ({
  anchorRef,
  children,
}: {
  anchorRef: React.RefObject<HTMLElement>;
  children: React.ReactNode;
}) => {
  const ttRef = useRef<HTMLDivElement>(null);
  const [style, setStyle] = useState<React.CSSProperties>({
    visibility: "hidden",
  });

  useEffect(() => {
    const anchor = anchorRef.current;
    const tt = ttRef.current;
    if (!anchor || !tt) return;

    const rect = anchor.getBoundingClientRect();
    const ttRect = tt.getBoundingClientRect();
    const pad = 8;

    // Prefer above; flip below if no room
    let top: number;
    if (rect.top - ttRect.height - pad >= 0) {
      top = rect.top - ttRect.height - pad;
    } else {
      top = rect.bottom + pad;
    }

    // Center horizontally; clamp to viewport
    let left = rect.left + rect.width / 2 - ttRect.width / 2;
    left = Math.max(
      pad,
      Math.min(left, window.innerWidth - ttRect.width - pad),
    );

    // Clamp vertically too
    top = Math.max(
      pad,
      Math.min(top, window.innerHeight - ttRect.height - pad),
    );

    setStyle({ top, left, visibility: "visible" });
  });

  return createPortal(
    <div ref={ttRef} className="churn-tt-portal" style={style}>
      {children}
    </div>,
    document.body,
  );
};

// ─── Tooltip Content ─────────────────────────────────────────────────────────

type CellTooltipProps = {
  cohort: Cohort;
  cell: CellData;
  periodLabel: string;
  periodMeta: PeriodMeta;
  cohortMeta: PeriodMeta | undefined;
  view: ViewMode;
  prevCell: CellData | undefined;
  t: Translations;
  viewConfig: ReturnType<typeof getViewConfig>;
};

const CellTooltip = ({
  cohort,
  cell,
  periodLabel,
  periodMeta,
  cohortMeta,
  view,
  prevCell,
  t,
  viewConfig,
}: CellTooltipProps) => {
  const canceled = getCanceled(cell, view);
  const remaining = cohort.start_count - canceled;
  const churnPct =
    cohort.start_count > 0
      ? Math.round((canceled / cohort.start_count) * 100)
      : 0;

  const prevCanceled = prevCell ? getCanceled(prevCell, view) : 0;
  const deltaCanceled = canceled - prevCanceled;

  const deltaAddon = prevCell
    ? cell.addon_canceled - prevCell.addon_canceled
    : cell.addon_canceled;
  const deltaBox = prevCell
    ? cell.box_canceled - prevCell.box_canceled
    : cell.box_canceled;

  return (
    <>
      <div className="tt-title">
        {cohort.start_period} {t.cohort} &rarr; {periodLabel}
      </div>
      {periodMeta.tea_addon_id && (
        <div className="tt-row">
          <span className="tt-label">{t.teaAddon}</span>
          <span className="tt-val" style={{ color: "#d6bcfa" }}>
            {periodMeta.tea_addon_id}
          </span>
        </div>
      )}
      <div className="tt-row">
        <span className="tt-label">{t.period}</span>
        <span className="tt-val" style={{ color: "#d6bcfa" }}>
          {periodMeta.date_range}
        </span>
      </div>
      <div className="tt-row">
        <span className="tt-label">{t.view}</span>
        <span className="tt-val" style={{ color: "#fbd38d" }}>
          {viewConfig[view].label}
        </span>
      </div>
      <div className="tt-sep" />
      <div className="tt-section">
        {t.cumulativeFromStart} ({cohort.start_count})
      </div>
      <div className="tt-row">
        <span className="tt-label">{t.remaining}</span>
        <span className="tt-val" style={{ color: "#68d391" }}>
          {remaining}
        </span>
      </div>
      {view === "all" && (
        <>
          <div className="tt-row">
            <span className="tt-label">{t.addonCanceledLabel}</span>
            <span className="tt-val tt-delta">{cell.addon_canceled}</span>
          </div>
          <div className="tt-row">
            <span className="tt-label">{t.boxCanceledLabel}</span>
            <span className="tt-val tt-delta">{cell.box_canceled}</span>
          </div>
          <div className="tt-row">
            <span className="tt-label">{t.totalCanceled}</span>
            <span className="tt-val tt-delta">{canceled}</span>
          </div>
        </>
      )}
      {view === "addon-only" && (
        <div className="tt-row">
          <span className="tt-label">{t.addonCanceledLabel}</span>
          <span className="tt-val tt-delta">{cell.addon_canceled}</span>
        </div>
      )}
      {view === "box-canceled" && (
        <div className="tt-row">
          <span className="tt-label">{t.boxCanceledLabel}</span>
          <span className="tt-val tt-delta">{cell.box_canceled}</span>
        </div>
      )}
      <div className="tt-row">
        <span className="tt-label">{t.churnRate}</span>
        <span className="tt-val tt-delta">{churnPct}%</span>
      </div>
      {deltaCanceled > 0 ? (
        <>
          <div className="tt-sep" />
          <div className="tt-section">{t.changeThisPeriod}</div>
          <div className="tt-row">
            <span className="tt-label">{t.newCancellations}</span>
            <span className="tt-val tt-delta">+{deltaCanceled}</span>
          </div>
          {view === "all" && deltaAddon > 0 && (
            <div className="tt-row tt-indent">
              <span className="tt-label">&darr; {t.addonCanceledLabel}</span>
              <span className="tt-val tt-delta">+{deltaAddon}</span>
            </div>
          )}
          {view === "all" && deltaBox > 0 && (
            <div className="tt-row tt-indent">
              <span className="tt-label">&darr; {t.boxCanceledLabel}</span>
              <span className="tt-val tt-delta">+{deltaBox}</span>
            </div>
          )}
        </>
      ) : (
        <>
          <div className="tt-sep" />
          <div className="tt-row">
            <span className="tt-label">{t.changeThisPeriod}</span>
            <span className="tt-val" style={{ color: "#a0aec0" }}>
              {t.noChange}
            </span>
          </div>
        </>
      )}
    </>
  );
};

// ─── Data Cell with hover tooltip ────────────────────────────────────────────

type DataCellProps = {
  bgColor: string;
  displayVal: string | number;
  subText: string;
  tooltipProps: CellTooltipProps;
  periodLabel: string;
  onClick: () => void;
  loading?: boolean;
};

const DataCell = ({
  bgColor,
  displayVal,
  subText,
  tooltipProps,
  periodLabel,
  onClick,
  loading,
}: DataCellProps) => {
  const [hovered, setHovered] = useState(false);
  const ref = useRef<HTMLTableCellElement>(null);

  if (loading) {
    return (
      <td className="churn-dc" style={{ background: bgColor }}>
        <Skeleton height={16} width={35} mb={2} mx="auto" />
        <Skeleton height={10} width={50} mx="auto" />
      </td>
    );
  }

  return (
    <td
      ref={ref}
      key={periodLabel}
      className="churn-dc"
      style={{ background: bgColor }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={onClick}
    >
      {displayVal}
      <div className="dc-sub">{subText}</div>
      {hovered && ref.current && (
        <PortalTooltip anchorRef={ref as React.RefObject<HTMLElement>}>
          <CellTooltip {...tooltipProps} />
        </PortalTooltip>
      )}
    </td>
  );
};

// ─── Styles (injected once) ──────────────────────────────────────────────────

const STYLES = `
.churn-table-wrap { position: relative; }

.churn-table { width: 100%; border-collapse: separate; border-spacing: 2px; font-size: 0.76rem; }
.churn-table th { padding: 6px 5px; text-align: center; border-radius: 4px; font-weight: 600; }
.churn-table td { padding: 7px 5px; text-align: center; border-radius: 4px; vertical-align: middle; }

.churn-table .th-month { font-size: 0.82rem; display: block; }
.churn-table .th-range { font-size: 0.65rem; display: block; margin-top: 1px; opacity: 0.7; }
.churn-table .th-addon { font-size: 0.65rem; display: block; margin-top: 1px; font-weight: 500; color: #805ad5; }

.churn-table .rh { font-weight: 600; text-align: left; padding: 6px 8px; white-space: nowrap; }
.churn-table .rh-month { font-size: 0.82rem; display: block; }
.churn-table .rh-range { font-size: 0.62rem; display: block; opacity: 0.7; }
.churn-table .rh-addon { font-size: 0.62rem; display: block; color: #805ad5; }

.churn-table .na-cell { opacity: 0.3; }
.churn-table .start-col { font-weight: 700; }

.churn-dc { position: relative; cursor: pointer; font-weight: 700; font-size: 0.88rem; transition: background 0.1s; border-radius: 4px; }
.churn-dc:hover { outline: 2px solid #4299e1; outline-offset: -1px; z-index: 5; }
.churn-dc .dc-sub { font-size: 0.65rem; font-weight: 400; opacity: 0.7; margin-top: 1px; }

.churn-tt-portal { position: fixed; z-index: 9999; pointer-events: none; background: #2d3748; color: #e2e8f0; padding: 12px 16px; border-radius: 8px; font-size: 0.75rem; white-space: nowrap; box-shadow: 0 4px 20px rgba(0,0,0,0.3); text-align: left; font-weight: 400; min-width: 260px; }

.churn-tt-portal .tt-title { font-weight: 700; color: #90cdf4; font-size: 0.8rem; margin-bottom: 6px; }
.churn-tt-portal .tt-sep { border-top: 1px solid #4a5568; margin: 5px 0; }
.churn-tt-portal .tt-row { display: flex; justify-content: space-between; gap: 16px; padding: 1px 0; }
.churn-tt-portal .tt-label { color: #a0aec0; }
.churn-tt-portal .tt-val { font-weight: 600; color: #fff; }
.churn-tt-portal .tt-delta { color: #fc8181; }
.churn-tt-portal .tt-section { font-size: 0.7rem; color: #a0aec0; text-transform: uppercase; letter-spacing: 0.3px; margin-top: 6px; margin-bottom: 2px; }
.churn-tt-portal .tt-indent { padding-left: 12px; }

.churn-table .totals-row td { font-weight: 700; font-size: 0.82rem; }

.view-tab-btn { padding: 10px 18px; border: 2px solid; cursor: pointer; font-size: 0.82rem; font-weight: 600; transition: all 0.15s; background: transparent; }
.view-tab-btn:first-child { border-radius: 8px 0 0 8px; }
.view-tab-btn:last-child { border-radius: 0 8px 8px 0; }
.view-tab-btn .view-desc { display: block; font-size: 0.68rem; font-weight: 400; opacity: 0.7; margin-top: 2px; }
`;

// ─── Subscription Row (for modal) ────────────────────────────────────────────

const formatDate = (d: string | null) => {
  if (!d) return "—";
  const dt = new Date(d);
  const day = String(dt.getDate()).padStart(2, "0");
  const month = String(dt.getMonth() + 1).padStart(2, "0");
  return `${day}/${month}/${dt.getFullYear()}`;
};

const brandColor: Record<string, string> = {
  tokyotreat: "pink",
  sakuraco: "grape",
  nomakenolife: "cyan",
  yumetwins: "violet",
};

const InfoTip = ({ text }: { text: string }) => (
  <HoverCard width={300} shadow="md" position="bottom" withArrow>
    <HoverCard.Target>
      <ActionIcon
        size="xs"
        variant="transparent"
        onClick={(e: React.MouseEvent) => e.stopPropagation()}
      >
        <IconInfoCircle
          size={14}
          style={{ opacity: 0.6, color: "currentColor" }}
        />
      </ActionIcon>
    </HoverCard.Target>
    <HoverCard.Dropdown style={{ color: "#1a202c" }}>
      <Text size="xs" style={{ lineHeight: 1.5, color: "#1a202c" }}>
        {text}
      </Text>
    </HoverCard.Dropdown>
  </HoverCard>
);

const DetailLabel = ({ children }: { children: React.ReactNode }) => (
  <Text size="xs" color="dimmed" style={{ minWidth: 70 }}>
    {children}
  </Text>
);

const DetailValue = ({ children }: { children: React.ReactNode }) => (
  <Text size="xs" weight={500}>
    {children}
  </Text>
);

const SubscriptionRow = ({
  sub,
  t,
}: {
  sub: CellDetailsSub;
  t: Translations;
}) => {
  const theme = useMantineTheme();
  const isDark = theme.colorScheme === "dark";
  const hasAddons = sub.tea_addons.length > 0;

  const paperBg = hasAddons
    ? undefined
    : isDark
      ? "rgba(245, 101, 101, 0.08)"
      : "rgba(254, 215, 215, 0.5)";

  return (
    <Paper
      p="md"
      withBorder
      radius="sm"
      style={{
        background: paperBg,
        borderColor: hasAddons
          ? undefined
          : isDark
            ? "rgba(245, 101, 101, 0.2)"
            : "#fed7d7",
      }}
    >
      <Group position="apart" mb="xs">
        <Group spacing="xs" align="center">
          <Text size="xs" color="dimmed" weight={700} style={{ width: 28 }}>
            {sub.number}
          </Text>
          <Text size="sm" weight={600}>
            {sub.name}
          </Text>
          <Text size="xs" color="dimmed">
            {sub.email}
          </Text>
          {sub.plan_brand && (
            <Badge
              size="xs"
              color={brandColor[sub.plan_brand] ?? "gray"}
              variant="light"
              ml={4}
            >
              {sub.plan_brand}
            </Badge>
          )}
        </Group>
        <Group spacing={6}>
          <Badge
            size="xs"
            color={sub.addon_canceled ? "orange" : "green"}
            variant={sub.addon_canceled ? "filled" : "outline"}
          >
            {sub.addon_canceled ? t.addonCanceled : t.addonActive}
          </Badge>
          <Badge
            size="xs"
            color={sub.box_canceled ? "red" : "green"}
            variant={sub.box_canceled ? "filled" : "outline"}
          >
            {sub.box_canceled ? t.boxCanceled : t.boxActive}
          </Badge>
        </Group>
      </Group>

      <Flex gap="xl" wrap="wrap" ml={28}>
        <Box>
          <Group spacing={6} mb={2}>
            <DetailLabel>{t.addon}</DetailLabel>
            <Anchor href={`/tea_addon_subscriptions/show/${sub.id}`} size="xs">
              #{sub.id}
            </Anchor>
          </Group>
          <Group spacing={6}>
            <DetailLabel>{t.started}</DetailLabel>
            <DetailValue>{formatDate(sub.addon_started_at)}</DetailValue>
          </Group>
          {sub.addon_canceled_at && (
            <Group spacing={6}>
              <DetailLabel>{t.canceled}</DetailLabel>
              <Text size="xs" color="orange" weight={500}>
                {formatDate(sub.addon_canceled_at)}
              </Text>
            </Group>
          )}
        </Box>

        <Box>
          <Group spacing={6} mb={2}>
            <DetailLabel>{t.box}</DetailLabel>
            <Anchor href={sub.chargebee_url} target="_blank" size="xs">
              {sub.chargebee_subscription_id}{" "}
              <IconExternalLink size={9} style={{ verticalAlign: "middle" }} />
            </Anchor>
          </Group>
          <Group spacing={6}>
            <DetailLabel>{t.status}</DetailLabel>
            <DetailValue>{sub.box_status}</DetailValue>
          </Group>
          {sub.box_canceled_at && (
            <Group spacing={6}>
              <DetailLabel>{t.canceled}</DetailLabel>
              <Text size="xs" color="red" weight={500}>
                {formatDate(sub.box_canceled_at)}
              </Text>
            </Group>
          )}
        </Box>

        {sub.plan_name && (
          <Box>
            <Group spacing={6}>
              <DetailLabel>{t.plan}</DetailLabel>
              <DetailValue>{sub.plan_name}</DetailValue>
            </Group>
          </Box>
        )}
      </Flex>

      {sub.tea_addons.length > 0 && (
        <Box
          ml={28}
          mt="xs"
          p="xs"
          style={{
            background: isDark
              ? "rgba(72, 187, 120, 0.08)"
              : "rgba(198, 246, 213, 0.4)",
            borderRadius: theme.radius.sm,
          }}
        >
          <Text size="xs" color="teal" weight={600} mb={4}>
            {t.teaAddons} ({sub.tea_addons.length})
          </Text>
          {sub.tea_addons.map((ta) => (
            <Group key={ta.id} spacing="xs" mb={2}>
              <Text size="xs" weight={500}>
                {ta.name}
              </Text>
              <Badge size="xs" color="violet" variant="light">
                {ta.chargebee_addon_id}
              </Badge>
              <Text size="xs" color="dimmed">
                {ta.box_month} — {t.shippable} {formatDate(ta.shippable_at)}
              </Text>
            </Group>
          ))}
        </Box>
      )}
      {sub.tea_addons.length === 0 && (
        <Box
          ml={28}
          mt="xs"
          p="xs"
          style={{
            background: isDark
              ? "rgba(245, 101, 101, 0.06)"
              : "rgba(254, 215, 215, 0.4)",
            borderRadius: theme.radius.sm,
          }}
        >
          <Text size="xs" color="red" weight={500}>
            {t.noMatchingTeaAddon}
          </Text>
        </Box>
      )}
    </Paper>
  );
};

// ─── Virtual List Modal ─────────────────────────────────────────────────────

type AddonFilter = "all" | "with_addons" | "without_addons";

const VirtualSubscriptionList = ({
  subscriptions,
  totalCount,
  isLoading,
  t,
}: {
  subscriptions: CellDetailsSub[];
  totalCount: number;
  isLoading: boolean;
  t: Translations;
}) => {
  const parentRef = useRef<HTMLDivElement>(null);
  const [addonFilter, setAddonFilter] = useState<AddonFilter>("all");

  const filtered = useMemo(() => {
    if (addonFilter === "with_addons")
      return subscriptions.filter((s) => s.tea_addons.length > 0);
    if (addonFilter === "without_addons")
      return subscriptions.filter((s) => s.tea_addons.length === 0);
    return subscriptions;
  }, [subscriptions, addonFilter]);

  const virtualizer = useVirtualizer({
    count: filtered.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 120,
    overscan: 10,
  });

  const withCount = subscriptions.filter((s) => s.tea_addons.length > 0).length;
  const withoutCount = subscriptions.length - withCount;

  return (
    <>
      <Group position="apart" mb="xs">
        <Text size="sm" color="dimmed">
          {isLoading
            ? `${t.loading} ${subscriptions.length} ${t.of} ${totalCount || "?"}`
            : `${filtered.length} ${t.of} ${subscriptions.length} ${subscriptions.length !== 1 ? t.subscriptions : t.subscription}`}
        </Text>
        <SegmentedControl
          size="xs"
          value={addonFilter}
          onChange={(v: string) => setAddonFilter(v as AddonFilter)}
          data={[
            { label: `${t.all} (${subscriptions.length})`, value: "all" },
            {
              label: `${t.withTeaAddons} (${withCount})`,
              value: "with_addons",
            },
            {
              label: `${t.withoutTeaAddons} (${withoutCount})`,
              value: "without_addons",
            },
          ]}
        />
      </Group>
      <div ref={parentRef} style={{ height: "70vh", overflow: "auto" }}>
        <div
          style={{
            height: virtualizer.getTotalSize(),
            width: "100%",
            position: "relative",
          }}
        >
          {virtualizer.getVirtualItems().map((virtualRow) => {
            const sub = filtered[virtualRow.index];
            return (
              <div
                key={sub.id}
                ref={virtualizer.measureElement}
                data-index={virtualRow.index}
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  transform: `translateY(${virtualRow.start}px)`,
                  paddingBottom: 8,
                }}
              >
                <SubscriptionRow sub={sub} t={t} />
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
};

// ─── Chart Loading Wrapper ───────────────────────────────────────────────────

const ChartSkeleton = ({ height }: { height: number }) => (
  <Box
    style={{
      height,
      display: "flex",
      flexDirection: "column",
      justifyContent: "flex-end",
      gap: 4,
      padding: "16px 0",
    }}
  >
    <Flex gap={6} align="flex-end" style={{ height: "100%" }}>
      {Array.from({ length: 12 }).map((_, i) => (
        <Skeleton
          key={i}
          style={{ flex: 1 }}
          height={`${30 + Math.sin(i * 0.8) * 25 + 25}%`}
          radius="xs"
        />
      ))}
    </Flex>
    <Skeleton height={8} width="100%" radius="xs" />
  </Box>
);

// ─── Main Component ──────────────────────────────────────────────────────────

export const TeaAddonSubscriptionDashboard = () => {
  const [yearPreset, setYearPreset] = useState<YearPreset>("2025");
  const [customFrom, setCustomFrom] = useState<string | null>(null);
  const [customTo, setCustomTo] = useState<string | null>(null);
  const [view, setView] = useState<ViewMode>("all");
  const [displayMode, setDisplayMode] = useState<DisplayMode>("counts");
  const [selectedCell, setSelectedCell] = useState<SelectedCell>(null);
  const [lang, setLang] = useState<Lang>("en");

  const t = T[lang];
  const viewConfig = useMemo(() => getViewConfig(t), [t]);

  const theme = useMantineTheme();
  const apiUrl = useApiUrl();
  const isDark = theme.colorScheme === "dark";

  const { from, to } = useMemo(() => {
    if (yearPreset === "custom" && customFrom && customTo) {
      return { from: customFrom, to: boxMonthEnd(customTo) };
    }
    return YEAR_RANGES[yearPreset] ?? YEAR_RANGES["all"];
  }, [yearPreset, customFrom, customTo]);

  const customReady = yearPreset !== "custom" || (!!customFrom && !!customTo);

  const { data, isLoading, isFetching } = useCustom<ChurnBreakdownResponse>({
    url: `${apiUrl}/tea_addon_subscription_analytics/churn_breakdown`,
    method: "get",
    config: {
      query: { from, to },
    },
    queryOptions: {
      enabled: customReady,
    },
  });

  const tableLoading = isLoading || isFetching;

  // Analytics data (graphs) — same date range, box month granularity
  const { data: analyticsRaw, isFetching: isGraphFetching } =
    useCustom<AnalyticsResponse>({
      url: `${apiUrl}/tea_addon_subscription_analytics`,
      method: "get",
      config: {
        query: { from, to, box_months: true },
      },
      queryOptions: {
        enabled: customReady,
      },
    });

  const analyticsData = analyticsRaw?.data?.data ?? [];
  const summary = analyticsRaw?.data?.summary ?? {
    total_active_subscribers: 0,
    total_renewals: 0,
    total_new_subscriptions: 0,
    total_cancellations: 0,
    average_churn_rate: 0,
    total_revenue: 0,
    total_loss_revenue: 0,
  };

  // Paginated cell details loading
  const [cellSubs, setCellSubs] = useState<CellDetailsSub[]>([]);
  const [cellTotalCount, setCellTotalCount] = useState(0);
  const [cellPage, setCellPage] = useState(0);
  const [isCellLoading, setIsCellLoading] = useState(false);
  const cellFetchingRef = useRef(false);

  // Reset when cell changes
  useEffect(() => {
    setCellSubs([]);
    setCellTotalCount(0);
    setCellPage(0);
    cellFetchingRef.current = false;
    if (selectedCell) {
      setCellPage(1);
    }
  }, [selectedCell]);

  // Fetch page
  useEffect(() => {
    if (!selectedCell || cellPage < 1 || cellFetchingRef.current) return;
    cellFetchingRef.current = true;
    setIsCellLoading(true);

    axiosInstance
      .get<CellDetailsResponse>(
        `${apiUrl}/tea_addon_subscription_analytics/cell_details`,
        {
          params: {
            cohort_start: selectedCell.cohortMeta.start,
            cohort_end: selectedCell.cohortMeta.end,
            period_start: selectedCell.periodMeta.start,
            mode: displayMode,
            view:
              displayMode === "counts" || displayMode === "rates"
                ? view
                : "all",
            page: cellPage,
            per_page: 200,
          },
        },
      )
      .then(({ data: json }) => {
        setCellSubs((prev) => [...prev, ...json.subscriptions]);
        setCellTotalCount(json.total_count);
        // Auto-fetch next page if more remain
        if (json.page < json.total_pages) {
          cellFetchingRef.current = false;
          setCellPage((p) => p + 1);
        } else {
          setIsCellLoading(false);
          cellFetchingRef.current = false;
        }
      })
      .catch(() => {
        setIsCellLoading(false);
        cellFetchingRef.current = false;
      });
  }, [selectedCell, cellPage, apiUrl, displayMode]);

  const breakdown = data?.data;
  const periods = breakdown?.periods ?? [];
  const cohorts = breakdown?.cohorts ?? [];
  const totals = breakdown?.totals ?? {};
  const actualAddonsTotals = breakdown?.actual_tea_addons_totals ?? {};
  const addonChainTotals = breakdown?.addon_chain_totals ?? {};

  const periodMetaMap = useMemo(() => {
    const map: Record<string, PeriodMeta> = {};
    periods.forEach((p) => {
      map[p.label] = p;
    });
    return map;
  }, [periods]);

  // Compute view-aware totals
  const viewTotals = useMemo(() => {
    const t: Record<string, number> = {};
    periods.forEach((p) => {
      t[p.label] = cohorts.reduce((sum, c) => {
        const cell = c.breakdown[p.label];
        if (!cell) return sum;
        return sum + getRemaining(c.start_count, cell, view);
      }, 0);
    });
    return t;
  }, [periods, cohorts, view]);

  const showStartCol = displayMode === "counts" || displayMode === "rates";
  const thBg = isDark ? theme.colors.dark[6] : "#edf2f7";
  const stickyBg = isDark ? theme.colors.dark[5] : "#edf2f7";
  const totalsBg = isDark ? theme.colors.dark[4] : "#edf2f7";
  const naBg = isDark ? theme.colors.dark[7] : "#f7fafc";
  const startColBg = isDark ? theme.colors.dark[7] : "#f7fafc";

  const chartColors = {
    newSubscriptions: isDark ? theme.colors.blue[4] : theme.colors.blue[6],
    renewals: isDark ? theme.colors.green[4] : theme.colors.green[6],
    active: isDark ? theme.colors.violet[4] : theme.colors.violet[6],
    cancellations: isDark ? theme.colors.red[4] : theme.colors.red[6],
    churnRate: isDark ? theme.colors.orange[4] : theme.colors.orange[6],
    revenue: isDark ? theme.colors.teal[4] : theme.colors.teal[6],
    lossRevenue: isDark ? theme.colors.pink[4] : theme.colors.pink[6],
  };

  const chartTooltipStyle = {
    contentStyle: {
      backgroundColor: isDark ? theme.colors.dark[7] : theme.white,
      border: `1px solid ${isDark ? theme.colors.dark[4] : theme.colors.gray[3]}`,
      borderRadius: theme.radius.sm,
    },
  };

  const fmtCurrency = (v: number) =>
    `$${(v / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const viewCfg = viewConfig[view];
  const bannerBgMap: Record<string, string> = isDark
    ? {
        blue: "rgba(49, 130, 206, 0.15)",
        orange: "rgba(237, 137, 54, 0.15)",
        red: "rgba(229, 62, 62, 0.15)",
      }
    : { blue: "#ebf8ff", orange: "#fffaf0", red: "#fff5f5" };
  const bannerBorderMap: Record<string, string> = isDark
    ? {
        blue: "rgba(49, 130, 206, 0.3)",
        orange: "rgba(237, 137, 54, 0.3)",
        red: "rgba(229, 62, 62, 0.3)",
      }
    : { blue: "#bee3f8", orange: "#fefcbf", red: "#fed7d7" };
  const bannerColorMap: Record<string, string> = isDark
    ? { blue: "#90cdf4", orange: "#fbd38d", red: "#feb2b2" }
    : { blue: "#2b6cb0", orange: "#975a16", red: "#c53030" };

  return (
    <Box>
      <style>{STYLES}</style>

      <Group position="apart" mb="lg">
        <Group spacing="md">
          <Title order={2}>{t.pageTitle}</Title>
          <SegmentedControl
            size="xs"
            value={lang}
            onChange={(v: string) => setLang(v as Lang)}
            data={[
              { label: "EN", value: "en" },
              { label: "JA", value: "ja" },
            ]}
          />
        </Group>
      </Group>

      {/* Year Selector */}
      <Paper p="md" mb="lg" withBorder>
        <Group>
          <SegmentedControl
            value={yearPreset}
            onChange={(value: string) => setYearPreset(value)}
            data={[
              ...YEAR_OPTIONS.map((yr) => ({ label: yr, value: yr })),
              { label: t.all, value: "all" },
              { label: t.custom, value: "custom" },
            ]}
          />
          {yearPreset === "custom" && (
            <>
              <Select
                placeholder={t.fromBoxMonth}
                data={BOX_MONTH_OPTIONS}
                value={customFrom}
                onChange={setCustomFrom}
                searchable
                size="sm"
                style={{ width: 170 }}
              />
              <Text size="sm" color="dimmed">
                →
              </Text>
              <Select
                placeholder={t.toBoxMonth}
                data={BOX_MONTH_OPTIONS.filter(
                  (o) => !customFrom || o.value >= customFrom,
                )}
                value={customTo}
                onChange={setCustomTo}
                searchable
                size="sm"
                style={{ width: 170 }}
              />
            </>
          )}
        </Group>
      </Paper>

      {/* Summary KPI Cards */}
      <SimpleGrid
        cols={5}
        mb="lg"
        breakpoints={[
          { maxWidth: "md", cols: 2 },
          { maxWidth: "sm", cols: 1 },
        ]}
      >
        {[
          {
            title: t.activeSubscribers,
            value: summary.total_active_subscribers.toLocaleString(),
            color: chartColors.active,
          },
          {
            title: t.totalRenewals,
            value: summary.total_renewals.toLocaleString(),
            color: chartColors.renewals,
          },
          {
            title: t.newSubscriptions,
            value: summary.total_new_subscriptions.toLocaleString(),
            color: chartColors.newSubscriptions,
          },
          {
            title: t.cancellations,
            value: summary.total_cancellations.toLocaleString(),
            color: chartColors.cancellations,
          },
          {
            title: t.avgChurnRate,
            value: `${summary.average_churn_rate}%`,
            color: chartColors.churnRate,
          },
        ].map((card) => (
          <Paper
            key={card.title}
            p="md"
            withBorder
            style={{ borderLeft: `4px solid ${card.color}` }}
          >
            <Text size="xs" color="dimmed" transform="uppercase" weight={700}>
              {isGraphFetching ? (
                <Skeleton height={10} width={80} />
              ) : (
                card.title
              )}
            </Text>
            <Text size="xl" weight={700} mt={4}>
              {isGraphFetching ? (
                <Skeleton height={24} width={60} />
              ) : (
                card.value
              )}
            </Text>
          </Paper>
        ))}
      </SimpleGrid>

      {/* Graphs */}
      <Grid mb="xl">
        <Grid.Col span={12}>
          <Paper p="md" withBorder>
            <Title order={4} mb="md">
              {t.activeSubscriptionsChart}
            </Title>
            {isGraphFetching ? (
              <ChartSkeleton height={300} />
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={analyticsData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" fontSize={12} />
                  <YAxis fontSize={12} />
                  <RechartsTooltip {...chartTooltipStyle} />
                  <Legend />
                  <Area
                    type="monotone"
                    dataKey="active_subscriptions"
                    name={t.activeSubscriptionsChart}
                    stroke={chartColors.active}
                    fill={chartColors.active}
                    fillOpacity={0.15}
                    strokeWidth={2}
                    dot={{ r: 3 }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </Paper>
        </Grid.Col>
        <Grid.Col span={12} md={6}>
          <Paper p="md" withBorder>
            <Title order={4} mb="md">
              {t.newSubscriptionsChart}
            </Title>
            {isGraphFetching ? (
              <ChartSkeleton height={280} />
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <AreaChart data={analyticsData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" fontSize={12} />
                  <YAxis fontSize={12} />
                  <RechartsTooltip {...chartTooltipStyle} />
                  <Legend />
                  <Area
                    type="monotone"
                    dataKey="new_subscriptions"
                    name={t.newSubscriptionsChart}
                    stroke={chartColors.newSubscriptions}
                    fill={chartColors.newSubscriptions}
                    fillOpacity={0.15}
                    strokeWidth={2}
                    dot={{ r: 3 }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </Paper>
        </Grid.Col>
        <Grid.Col span={12} md={6}>
          <Paper p="md" withBorder>
            <Title order={4} mb="md">
              {t.renewalsChart}
            </Title>
            {isGraphFetching ? (
              <ChartSkeleton height={280} />
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <AreaChart data={analyticsData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" fontSize={12} />
                  <YAxis fontSize={12} />
                  <RechartsTooltip {...chartTooltipStyle} />
                  <Legend />
                  <Area
                    type="monotone"
                    dataKey="renewals"
                    name={t.renewalsChart}
                    stroke={chartColors.renewals}
                    fill={chartColors.renewals}
                    fillOpacity={0.15}
                    strokeWidth={2}
                    dot={{ r: 3 }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </Paper>
        </Grid.Col>
        <Grid.Col span={12} md={6}>
          <Paper p="md" withBorder>
            <Group position="apart" mb="md">
              <Title order={4}>{t.revenueChart}</Title>
              <Paper p="xs" px="md" withBorder radius="md">
                <Text size="xs" color="dimmed">
                  {t.total}
                </Text>
                <Text size="lg" weight={700} color="teal">
                  {isGraphFetching ? (
                    <Skeleton height={20} width={60} />
                  ) : (
                    fmtCurrency(summary.total_revenue)
                  )}
                </Text>
              </Paper>
            </Group>
            {isGraphFetching ? (
              <ChartSkeleton height={280} />
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <AreaChart data={analyticsData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" fontSize={12} />
                  <YAxis fontSize={12} tickFormatter={(v) => fmtCurrency(v)} />
                  <RechartsTooltip
                    {...chartTooltipStyle}
                    formatter={(v: number | undefined) => [
                      fmtCurrency(v ?? 0),
                      t.revenueChart,
                    ]}
                  />
                  <Legend />
                  <Area
                    type="monotone"
                    dataKey="revenue"
                    name={t.revenueChart}
                    stroke={chartColors.revenue}
                    fill={chartColors.revenue}
                    fillOpacity={0.15}
                    strokeWidth={2}
                    dot={{ r: 3 }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </Paper>
        </Grid.Col>
        <Grid.Col span={12} md={6}>
          <Paper p="md" withBorder>
            <Group position="apart" mb="md">
              <Title order={4}>{t.lossRevenueChart}</Title>
              <Paper p="xs" px="md" withBorder radius="md">
                <Text size="xs" color="dimmed">
                  {t.total}
                </Text>
                <Text size="lg" weight={700} color="pink">
                  {isGraphFetching ? (
                    <Skeleton height={20} width={60} />
                  ) : (
                    fmtCurrency(summary.total_loss_revenue)
                  )}
                </Text>
              </Paper>
            </Group>
            {isGraphFetching ? (
              <ChartSkeleton height={280} />
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <AreaChart data={analyticsData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" fontSize={12} />
                  <YAxis fontSize={12} tickFormatter={(v) => fmtCurrency(v)} />
                  <RechartsTooltip
                    {...chartTooltipStyle}
                    formatter={(v: number | undefined) => [
                      fmtCurrency(v ?? 0),
                      t.lossRevenueChart,
                    ]}
                  />
                  <Legend />
                  <Area
                    type="monotone"
                    dataKey="loss_revenue"
                    name={t.lossRevenueChart}
                    stroke={chartColors.lossRevenue}
                    fill={chartColors.lossRevenue}
                    fillOpacity={0.15}
                    strokeWidth={2}
                    dot={{ r: 3 }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </Paper>
        </Grid.Col>
        <Grid.Col span={12} md={6}>
          <Paper p="md" withBorder>
            <Title order={4} mb="md">
              {t.cancellationsChart}
            </Title>
            {isGraphFetching ? (
              <ChartSkeleton height={280} />
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={analyticsData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" fontSize={12} />
                  <YAxis fontSize={12} />
                  <RechartsTooltip {...chartTooltipStyle} />
                  <Legend />
                  <Bar
                    dataKey="cancellations"
                    name={t.cancellationsChart}
                    fill={chartColors.cancellations}
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            )}
          </Paper>
        </Grid.Col>
        <Grid.Col span={12} md={6}>
          <Paper p="md" withBorder>
            <Title order={4} mb="md">
              {t.churnRateChart}
            </Title>
            {isGraphFetching ? (
              <ChartSkeleton height={280} />
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={analyticsData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" fontSize={12} />
                  <YAxis fontSize={12} unit="%" />
                  <RechartsTooltip
                    {...chartTooltipStyle}
                    formatter={(v: number | undefined) => [
                      `${v ?? 0}%`,
                      t.churnRateChart,
                    ]}
                  />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="churn_rate"
                    name={t.churnRateChart}
                    stroke={chartColors.churnRate}
                    strokeWidth={2}
                    dot={{ r: 3 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
          </Paper>
        </Grid.Col>
        <Grid.Col span={12}>
          <Paper p="md" withBorder>
            <Title order={4} mb="md">
              {t.teaAddonsCreatedChart}
            </Title>
            {isGraphFetching ? (
              <ChartSkeleton height={350} />
            ) : (
              <ResponsiveContainer width="100%" height={350}>
                <BarChart data={analyticsData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="date"
                    fontSize={11}
                    tick={({ x, y, payload }: any) => {
                      const d = analyticsData.find(
                        (p: AnalyticsDataPoint) => p.date === payload.value,
                      );
                      return (
                        <g transform={`translate(${x},${y})`}>
                          <text
                            x={0}
                            y={0}
                            dy={12}
                            textAnchor="middle"
                            fontSize={11}
                            fill={isDark ? "#a0aec0" : "#4a5568"}
                          >
                            {payload.value}
                          </text>
                          {d?.addon_name && (
                            <text
                              x={0}
                              y={0}
                              dy={24}
                              textAnchor="end"
                              fontSize={9}
                              fill={isDark ? "#b794f4" : "#805ad5"}
                              transform="rotate(-35)"
                            >
                              {d.addon_name}
                            </text>
                          )}
                        </g>
                      );
                    }}
                    height={65}
                  />
                  <YAxis fontSize={12} />
                  <RechartsTooltip
                    {...chartTooltipStyle}
                    formatter={(
                      value: number | undefined,
                      _: any,
                      props: any,
                    ) => {
                      const d = props.payload as AnalyticsDataPoint;
                      return [value, d.addon_name || t.teaAddonsCreatedChart];
                    }}
                  />
                  <Legend />
                  <Bar
                    dataKey="tea_addon_count"
                    name={t.teaAddonsCreatedChart}
                    fill={isDark ? "#b794f4" : "#805ad5"}
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            )}
          </Paper>
        </Grid.Col>
      </Grid>

      {/* Churn Breakdown Section */}
      <Group position="apart" mb="md">
        <Group spacing={4}>
          <Title order={3}>{t.churnBreakdown}</Title>
          <InfoTip text={t.helpChurnBreakdown} />
        </Group>
        <Button
          leftIcon={<IconDownload size={16} />}
          variant="outline"
          size="sm"
          onClick={() =>
            breakdown && downloadCSV(breakdown, view, displayMode, from, to)
          }
          disabled={!breakdown || periods.length === 0}
        >
          {t.downloadCsv}
        </Button>
      </Group>

      {/* Display Mode Tabs */}
      <Group spacing={0} mb="md">
        {(
          [
            {
              value: "counts",
              label: t.activeCounts,
              help: t.helpActiveCounts,
            },
            { value: "rates", label: t.churnPct, help: t.helpChurnPct },
            {
              value: "actual_addons",
              label: t.actualAddons,
              help: t.helpActualAddons,
            },
            {
              value: "addon_chain",
              label: t.addonChain,
              help: t.helpAddonChain,
            },
          ] as const
        ).map((tab, idx, arr) => (
          <Button
            key={tab.value}
            size="sm"
            variant={displayMode === tab.value ? "filled" : "default"}
            onClick={() => setDisplayMode(tab.value as DisplayMode)}
            style={{
              borderRadius:
                idx === 0
                  ? "4px 0 0 4px"
                  : idx === arr.length - 1
                    ? "0 4px 4px 0"
                    : 0,
            }}
            rightIcon={<InfoTip text={tab.help} />}
          >
            {tab.label}
          </Button>
        ))}
      </Group>

      {/* View Tabs — only relevant for Active Counts and Churn % */}
      {(displayMode === "counts" || displayMode === "rates") && (
        <>
          <Flex gap={0} mb="md">
            {(
              [
                { key: "all" as ViewMode, help: t.helpAllCancellations },
                {
                  key: "addon-only" as ViewMode,
                  help: t.helpAddonCanceledBoxActive,
                },
                {
                  key: "box-canceled" as ViewMode,
                  help: t.helpAddonCanceledBoxCanceled,
                },
              ] as const
            ).map(({ key, help }, idx, arr) => {
              const cfg = viewConfig[key];
              const isActive = view === key;
              return (
                <button
                  key={key}
                  className="view-tab-btn"
                  onClick={() => setView(key)}
                  style={{
                    borderColor: isActive
                      ? isDark
                        ? theme.colors.gray[3]
                        : "#2d3748"
                      : isDark
                        ? theme.colors.dark[4]
                        : "#e2e8f0",
                    background: isActive
                      ? isDark
                        ? theme.colors.dark[5]
                        : "#2d3748"
                      : "transparent",
                    color: isActive
                      ? "#fff"
                      : isDark
                        ? theme.colors.gray[4]
                        : "inherit",
                    borderRadius:
                      idx === 0
                        ? "8px 0 0 8px"
                        : idx === arr.length - 1
                          ? "0 8px 8px 0"
                          : undefined,
                    display: "flex",
                    alignItems: "center",
                    gap: 4,
                  }}
                >
                  <span>
                    {cfg.label}
                    <span className="view-desc">{cfg.description}</span>
                  </span>
                  <InfoTip text={help} />
                </button>
              );
            })}
          </Flex>

          <Paper
            p="xs"
            px="md"
            mb="md"
            style={{
              background: bannerBgMap[viewCfg.bannerColor],
              border: `1px solid ${bannerBorderMap[viewCfg.bannerColor]}`,
              color: bannerColorMap[viewCfg.bannerColor],
              borderRadius: theme.radius.sm,
              fontSize: "0.82rem",
              fontWeight: 500,
            }}
          >
            {viewCfg.bannerText}
          </Paper>
        </>
      )}

      {/* Table */}
      <Paper p="md" withBorder>
        <Box style={{ position: "relative" }}>
          {tableLoading && periods.length === 0 && (
            <ScrollArea>
              <table className="churn-table">
                <thead>
                  <tr>
                    <th style={{ minWidth: 120, background: thBg }}>
                      <Skeleton height={14} width={60} />
                    </th>
                    {showStartCol && (
                      <th style={{ background: thBg, minWidth: 70 }}>
                        <Skeleton height={14} width={35} />
                      </th>
                    )}
                    {Array.from({ length: 12 }).map((_, i) => (
                      <th key={i} style={{ minWidth: 75, background: thBg }}>
                        <Skeleton height={14} width={55} mb={4} />
                        <Skeleton height={10} width={70} />
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {Array.from({ length: 8 }).map((_, ri) => (
                    <tr key={ri}>
                      <td style={{ background: stickyBg }}>
                        <Skeleton height={14} width={70} />
                      </td>
                      {showStartCol && (
                        <td style={{ background: startColBg }}>
                          <Skeleton height={14} width={30} />
                        </td>
                      )}
                      {Array.from({ length: 12 }).map((_, ci) => (
                        <td key={ci}>
                          <Skeleton height={16} width={35} mb={2} />
                          <Skeleton height={10} width={50} />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </ScrollArea>
          )}

          {periods.length > 0 && (
            <ScrollArea>
              <table className="churn-table">
                <thead>
                  <tr>
                    <th
                      style={{
                        minWidth: 120,
                        background: thBg,
                        position: "sticky",
                        left: 0,
                        zIndex: 2,
                      }}
                    >
                      {t.cohort}
                    </th>
                    {showStartCol && (
                      <th
                        style={{
                          background: thBg,
                          position: "sticky",
                          left: 120,
                          zIndex: 2,
                          minWidth: 70,
                        }}
                      >
                        {t.start}
                      </th>
                    )}
                    {periods.map((p) => (
                      <th
                        key={p.label}
                        style={{ minWidth: 75, background: thBg }}
                      >
                        <span className="th-month">{p.label}</span>
                        <span className="th-range">{p.date_range}</span>
                        {p.tea_addon_id && (
                          <span className="th-addon">{p.tea_addon_id}</span>
                        )}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {cohorts.map((cohort) => {
                    const cohortMeta = periodMetaMap[cohort.start_period];
                    return (
                      <tr key={cohort.start_period}>
                        <td
                          className="rh"
                          style={{
                            background: stickyBg,
                            position: "sticky",
                            left: 0,
                            zIndex: 1,
                          }}
                        >
                          <span className="rh-month">
                            {cohort.start_period}
                          </span>
                        </td>
                        {showStartCol && (
                          <td
                            className="start-col"
                            style={{
                              background: startColBg,
                              position: "sticky",
                              left: 120,
                              zIndex: 1,
                            }}
                          >
                            {tableLoading ? (
                              <Skeleton height={14} width={30} />
                            ) : (
                              cohort.start_count
                            )}
                          </td>
                        )}
                        {periods.map((p, pIdx) => {
                          const cell = cohort.breakdown[p.label];
                          if (!cell) {
                            return (
                              <td
                                key={p.label}
                                className="na-cell"
                                style={{ background: naBg }}
                              >
                                &mdash;
                              </td>
                            );
                          }

                          const canceled = getCanceled(cell, view);
                          const remaining = cohort.start_count - canceled;
                          const churnPct =
                            cohort.start_count > 0
                              ? Math.round(
                                  (canceled / cohort.start_count) * 100,
                                )
                              : 0;
                          const useChurnColors =
                            displayMode === "counts" || displayMode === "rates";
                          const colorClass = useChurnColors
                            ? churnColorClass(churnPct)
                            : "";
                          const bgColor =
                            useChurnColors && colorClass
                              ? isDark
                                ? COLOR_MAP_DARK[colorClass]
                                : COLOR_MAP[colorClass]
                              : undefined;

                          const displayVal =
                            displayMode === "counts"
                              ? remaining
                              : displayMode === "rates"
                                ? `${churnPct}%`
                                : displayMode === "actual_addons"
                                  ? cell.actual_tea_addons
                                  : cell.addon_chain;
                          const subText =
                            displayMode === "counts"
                              ? `${churnPct}% ${t.churnSuffix}`
                              : displayMode === "rates"
                                ? `${remaining} ${t.remainingSuffix}`
                                : "";

                          // Find previous cell for delta computation
                          const prevPeriodLabel =
                            pIdx > 0 ? periods[pIdx - 1].label : undefined;
                          const prevCell = prevPeriodLabel
                            ? cohort.breakdown[prevPeriodLabel]
                            : undefined;

                          return (
                            <DataCell
                              key={p.label}
                              bgColor={bgColor ?? ""}
                              displayVal={displayVal}
                              subText={subText}
                              periodLabel={p.label}
                              loading={tableLoading}
                              onClick={() =>
                                cohortMeta &&
                                setSelectedCell({
                                  cohort,
                                  cohortMeta,
                                  periodMeta: p,
                                })
                              }
                              tooltipProps={{
                                cohort,
                                cell,
                                periodLabel: p.label,
                                periodMeta: p,
                                cohortMeta,
                                view,
                                prevCell,
                                t,
                                viewConfig,
                              }}
                            />
                          );
                        })}
                      </tr>
                    );
                  })}

                  {/* Totals Row */}
                  <tr className="totals-row">
                    <td
                      className="rh"
                      style={{
                        background: totalsBg,
                        position: "sticky",
                        left: 0,
                        zIndex: 1,
                        fontWeight: 700,
                      }}
                    >
                      <span className="rh-month">{t.total}</span>
                    </td>
                    {showStartCol && (
                      <td
                        style={{
                          background: totalsBg,
                          position: "sticky",
                          left: 120,
                          zIndex: 1,
                        }}
                      />
                    )}
                    {periods.map((p) => (
                      <td
                        key={p.label}
                        style={{ fontWeight: 700, background: totalsBg }}
                      >
                        {tableLoading ? (
                          <Skeleton height={14} width={35} />
                        ) : displayMode === "actual_addons" ? (
                          (actualAddonsTotals[p.label] ?? 0).toLocaleString()
                        ) : displayMode === "addon_chain" ? (
                          (addonChainTotals[p.label] ?? 0).toLocaleString()
                        ) : (
                          (viewTotals[p.label] ?? 0).toLocaleString()
                        )}
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </ScrollArea>
          )}
        </Box>
      </Paper>

      {/* Cell Details Modal */}
      <Modal
        opened={!!selectedCell}
        onClose={() => setSelectedCell(null)}
        title={
          selectedCell
            ? `${selectedCell.cohort.start_period} ${t.cohort} — ${selectedCell.periodMeta.label}`
            : ""
        }
        size="95vw"
        styles={{
          body: { overflow: "hidden" },
        }}
      >
        <VirtualSubscriptionList
          subscriptions={cellSubs}
          totalCount={cellTotalCount}
          isLoading={isCellLoading}
          t={t}
        />
      </Modal>
    </Box>
  );
};
